import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Folder } from '@efficiently/models';

import { FoldersRepository } from '@efficiently/client-api-content';
import { GraphQLClient, GraphqlExecutorService } from '@efficiently/client-core-graphql';

import { GET_CONTENT_FOLDER_COUNTS, GET_FOLDERS } from '../graphql/queries';

// TEMP-CODEGEN-FRONTIER(EFF-24232): the folder operations are typed against the generated schema, but the
// abstract repo still exposes the domain `Folder` shape (and the plain `{ alias; count }` counts shape)
// consumed app-wide. Every `as unknown as` in this file is that generated↔domain boundary and MUST stay
// confined to it — a cast that is not a frontier cast does not belong here. Remove them as the folder
// consumers migrate. This repo-edge frontier is the sanctioned exception to the cast-free rule; see
// docs/common/graphql-codegen-development.md.
@Injectable()
export class FoldersGqlRepository extends FoldersRepository {
	private readonly gql = inject(GraphqlExecutorService);

	/**
	 * Loads the folders of a project.
	 *
	 * @param projectId - Id of the project whose folders are requested.
	 * @param skipGlobalErrorHandler - When `true`, a failed request is not routed to the
	 * global error handler (no user-facing error notification). This flag exists because
	 * for some flows a folders failure is non-critical and should stay silent:
	 * - background/prefetch loads of folders where a failure has no visible impact;
	 * - cases where a generated image simply does not get attached to a folder — mainly
	 *   logo handling — and losing that attachment is acceptable;
	 * - the first attempt to load folders when entering snippet mode on plans.
	 *
	 * Use it only for such non-critical, background scenarios. Leave it `false` (default)
	 * where the failure must surface via the global handler.
	 */
	public getFolders(projectId: string, skipGlobalErrorHandler: boolean = false): Observable<Folder[]> {
		return this.gql.watchOnce(GET_FOLDERS, {
			variables: { projectId },
			client: GraphQLClient.Core,
			skipGlobalErrorHandler,
			// silent request and incorrectly bypass the global error handler.
			queryDeduplication: skipGlobalErrorHandler ? false : undefined,
			select: data => data.folders as unknown as Folder[],
		});
	}

	// TODO EFF-13647 - add error handling
	public getContentFolderCounts(
		projectId: string,
		folderIds: string[],
	): Observable<{ alias: string; count: number }[]> {
		return this.gql.watchOnce(GET_CONTENT_FOLDER_COUNTS, {
			variables: { projectId, folderIds },
			client: GraphQLClient.Core,
			select: data => data.contentFolderCounts as unknown as { alias: string; count: number }[],
		});
	}
}
